import React, { HTMLProps, useCallback, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import CollapseIcon from '../../content/CollapseIcon';
import ExpandIcon from '../../content/ExpandIcon';
import Button from '../Button';
import LabeledTextbox from '../LabeledTextbox';

export const GiftCardTestId = 'GiftCard-input';

export type GiftCardProps = {
  isApplyingGiftCard?: boolean;
  appliedGiftCards?: string[];
  defaultValue?: string;
  onGiftCardApplyClicked: (giftCardCode: string) => Promise<string | undefined>;
  setApplyButtonEnabled?: (enabled: boolean) => void;
  clearGiftCard?: boolean;
};

function getGiftCardCodeWithoutDashes(giftCardCode = ''): string {
  return giftCardCode.replace(/[_-]/g, '');
}

const GiftCard: React.FC<GiftCardProps> = ({
  isApplyingGiftCard = false,
  appliedGiftCards = [],
  defaultValue,
  onGiftCardApplyClicked,
  setApplyButtonEnabled,
  clearGiftCard,
}: GiftCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonAnimationClass, setButtonAnimationClass] = useState('');
  const [contentOpacityAnimationClass, setContentOpacityAnimation] =
    useState('');
  const [giftCardCode, setGiftCard] = useState<string | undefined>(
    (defaultValue || '').toUpperCase()
  );

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const rawGiftCardCode = getGiftCardCodeWithoutDashes(giftCardCode);
  const [isGiftCardAlreadyApplied, setIsGiftCardAlreadyApplied] =
    useState<boolean>(
      Boolean(
        rawGiftCardCode &&
          !!appliedGiftCards.find(
            code => code.toUpperCase() === rawGiftCardCode.toUpperCase()
          )
      )
    );
  const [warningMessage, setWarningMessage] = useState<string | undefined>(
    isGiftCardAlreadyApplied
      ? `Gift Card "${giftCardCode}" is already applied.`
      : undefined
  );

  const checkIfGiftCardIsAlreadyApplied = useCallback((): void => {
    const rawGiftCardCodeEntered = getGiftCardCodeWithoutDashes(giftCardCode);
    if (
      rawGiftCardCodeEntered &&
      !!appliedGiftCards.find(
        code => code.toUpperCase() === rawGiftCardCodeEntered.toUpperCase()
      )
    ) {
      setWarningMessage(`Gift Card "${giftCardCode}" is already applied.`);
      setIsGiftCardAlreadyApplied(true);
    } else {
      setWarningMessage('');
      setIsGiftCardAlreadyApplied(false);
    }
  }, [giftCardCode, appliedGiftCards]);

  useEffect(() => {
    checkIfGiftCardIsAlreadyApplied();
  }, [giftCardCode, appliedGiftCards, checkIfGiftCardIsAlreadyApplied]);

  const isValidGiftCardCode = Boolean(rawGiftCardCode.length === 13);
  const applyButtonDisabled =
    !isValidGiftCardCode || isApplyingGiftCard || isGiftCardAlreadyApplied;

  useEffect(() => {
    if (clearGiftCard) {
      setGiftCard('');
      setWarningMessage('');
    }
    setApplyButtonEnabled?.(!applyButtonDisabled);
  }, [applyButtonDisabled, setApplyButtonEnabled, clearGiftCard]);

  const handleApplyGiftCardClicked = async () => {
    if (!giftCardCode) {
      return;
    }
    setErrorMessage('');
    setWarningMessage('');
    const response = await onGiftCardApplyClicked(rawGiftCardCode);
    if ((response ?? '').length > 0) {
      setErrorMessage(response);
    } else {
      setGiftCard('');
      setWarningMessage('');
    }
  };

  const handleGiftCardChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setGiftCard(value.toUpperCase());
  };

  const handleGiftCardKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isGiftCardAlreadyApplied) {
      handleApplyGiftCardClicked();
    }
  };

  const handleExpandCollapseButtonClicked = () => {
    setButtonAnimationClass(!isOpen ? 'animate-expand' : 'animate-collapse');
    setContentOpacityAnimation(
      !isOpen ? 'animate-appear' : 'animate-disappear'
    );
  };

  const handleButtonAnimationEnd = () => {
    setButtonAnimationClass('');
    setContentOpacityAnimation('');
    setIsOpen(value => {
      // when closing empty giftCardCode and errorMessage should be cleared
      if (value) {
        setGiftCard('');
        setErrorMessage('');
        setWarningMessage('');
      }
      return !value;
    });
  };

  return (
    <div className="flex flex-row" data-testid={GiftCardTestId}>
      <button
        type="button"
        className="portrait:pt-0 sm:pt-2"
        onClick={handleExpandCollapseButtonClicked}
        onAnimationEnd={handleButtonAnimationEnd}
      >
        <div className="px-2 flex flex-row pb-4 h-16 justify-items-center">
          <span
            className={`self-center pt-2 mt-1 ${buttonAnimationClass} ${
              isOpen && 'mt-2'
            }`}
          >
            {!isOpen && <ExpandIcon />}
            {isOpen && <CollapseIcon />}
          </span>
          {!isOpen && (
            <span className="pt-1 pl-2 mt-2 self-center">
              {appliedGiftCards.length === 0
                ? 'Add Gift Card'
                : 'Add Another Gift Card'}
            </span>
          )}
        </div>
      </button>
      {isOpen && (
        <div className={`flex flex-col w-full ${contentOpacityAnimationClass}`}>
          <div className="flex flex-row">
            <span className="w-3/4 self-center">
              <InputMask
                mask="****-*****-****"
                onChange={handleGiftCardChanged}
                value={giftCardCode || ''}
              >
                {(inputProps: HTMLProps<HTMLElement>) => (
                  <LabeledTextbox
                    value={inputProps.value}
                    className="mb-0"
                    inputClassName="uppercase"
                    label="13-character Gift Certificate"
                    name="giftCardCode"
                    errorLabel={errorMessage}
                    onKeyPress={handleGiftCardKeyPress}
                    autoFocus
                  />
                )}
              </InputMask>
            </span>
            <Button
              className="self-center mt-3"
              isDisabled={applyButtonDisabled}
              onClick={handleApplyGiftCardClicked}
            >
              Apply
            </Button>
          </div>
          {warningMessage && !isApplyingGiftCard && (
            <span className="text-sm pl-2">{warningMessage}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftCard;
